import React, { useCallback, useEffect, useState } from 'react';
import sortBy from 'lodash/sortBy';
import flatten from 'lodash/flatten';

import api from '../../../../api';
import config from '../../../../config';
import _ from '@ihme/common/locale';
import locale from '../locale';
import View from './View';
import type { ChartSeries, DataResponse } from '../../../../types/contracts';

type Props = {
    locationId: number;
};

const COLORS: Record<string | number, string> = {
    '400,418,419,522,523': '#E86D0C',
    297: '#50C3F7',
    298: '#199949',
    345: '#FBDD39',
};

const parseResponse = (
    response: DataResponse,
    causeId: number,
    locationId: number,
    axisId: number
): ChartSeries => {
    const { columns, records } = response;

    const yearIndex = columns.indexOf('year');
    const valueIndex = columns.indexOf('value');
    const causeIndex = columns.indexOf('cause_id');
    const locationIndex = columns.indexOf('location_id');

    return {
        id: causeId,
        color: COLORS[causeId],
        name: _(`cause_${causeId}`),
        locationId,
        axisId,
        values: sortBy(records, yearIndex)
            .filter(
                record => record[causeIndex] === causeId && record[locationIndex] === locationId
            )
            .map(record => ({
                year: record[yearIndex] as number,
                value: record[valueIndex] as number,
                locationId,
            })),
    };
};

const DeathsChart = ({ locationId }: Props) => {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<ChartSeries[]>([]);

    const loadData = useCallback((nextLocationId: number) => {
        setIsLoading(true);

        const locationIds = [config.globalLocationId, nextLocationId];
        const causeIds = [
            config.tuberculosisCauseId,
            config.hivAidsCauseId,
            config.malariaCauseId,
            config.hepatitisCauseId,
        ];

        const criteria = {
            location_id: locationIds.join(','),
            age_group_id: config.allAgesGroupId,
            gender_id: config.bothGendersId,
            measure_id: config.deathMeasureId,
            metric_id: config.numberMetricId,
            cause_id: causeIds.join(','),
        };

        api.data
            .getCauseOutcome(criteria)
            .then((response: DataResponse) => {
                const nextData = flatten(
                    locationIds.map((location, i) =>
                        causeIds.map(causeId => parseResponse(response, causeId, location, i))
                    )
                );

                setData(nextData);
                setIsLoading(false);
            })
            .catch(error => {
                // Keep prior behavior (log) while avoiding unhandled promise rejection.
                console.log(error);
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        loadData(locationId);
    }, [locationId, loadData]);

    return (
        <View
            title={_(locale.chartTitle)}
            data={data}
            isLoading={isLoading}
            locationId={locationId}
        />
    );
};

export default DeathsChart;
